/**
 * Minimal typings for the WebMCP model context API.
 * The current proposal exposes `document.modelContext`; earlier previews used
 * `navigator.modelContext`. We accept either.
 */

export interface WebMcpToolResult {
  content: Array<{ type: "text"; text: string }>;
  isError?: boolean;
}

export interface WebMcpToolDescriptor {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  annotations?: Record<string, unknown>;
  execute: (args: Record<string, any>) => Promise<WebMcpToolResult>;
}

export interface WebMcpRegistration {
  unregister?: () => void;
  destroy?: () => void;
}

export interface ModelContext {
  registerTool?: (tool: WebMcpToolDescriptor) => WebMcpRegistration | void;
  provideContext?: (context: { tools: WebMcpToolDescriptor[] }) => void;
}

function usable(mc: ModelContext | undefined): ModelContext | null {
  return mc && (typeof mc.registerTool === "function" || typeof mc.provideContext === "function")
    ? mc
    : null;
}

export function getModelContext(): ModelContext | null {
  if (typeof document !== "undefined") {
    const fromDocument = usable(
      (document as Document & { modelContext?: ModelContext }).modelContext,
    );
    if (fromDocument) return fromDocument;
  }
  if (typeof navigator !== "undefined") {
    return usable((navigator as Navigator & { modelContext?: ModelContext }).modelContext);
  }
  return null;
}

export function isWebMcpSupported(): boolean {
  return getModelContext() !== null;
}
